import React, { useState } from 'react';
import { proverbs, Proverb } from '../data/proverbs';
import { isAudioSupported, playSmartAudio } from '../utils/audioUtils';

const SET_TITLE = 'Moonlight in a Teacup';
const SET_SUBTITLE = 'Timeless Persian Proverbs for Learners in Love with Language';

const cardBorder = 'border-2 border-amber-400';
const cardShadow = 'shadow-xl';
const persianFont = 'font-farsi text-4xl md:text-5xl font-extrabold';
const divider = <div className="my-4 flex justify-center"><span className="inline-block w-24 h-1 rounded-full bg-amber-300 opacity-60"></span></div>;

const ProverbFlashcards: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);

  const currentCard: Proverb | undefined = proverbs[currentIndex];

  const handleFlip = () => setShowAnswer((prev) => !prev);
  const handleNext = () => {
    setShowAnswer(false);
    setCurrentIndex((prev) => (prev + 1) % proverbs.length);
  };
  const handlePrev = () => {
    setShowAnswer(false);
    setCurrentIndex((prev) => (prev - 1 + proverbs.length) % proverbs.length);
  };
  const handlePlayAudio = async () => {
    if (!currentCard) return;
    setAudioLoading(true);
    try {
      if (!isAudioSupported()) {
        alert('Audio pronunciation is not supported in your browser.');
        return;
      }
      await playSmartAudio(undefined, currentCard.persian);
    } catch (e) {
      alert('Failed to play audio.');
    } finally {
      setAudioLoading(false);
    }
  };

  if (!currentCard) return null;

  return (
    <div className="space-y-8 max-w-xl mx-auto">
      {/* Set Title */}
      <div className="text-center mt-8">
        <h1 className="text-4xl font-extrabold text-rose-700 mb-2 tracking-tight drop-shadow-lg">
          {SET_TITLE} <span className="text-2xl">🌙</span>
        </h1>
        <p className="text-lg text-gray-700 dark:text-gray-200 italic mb-4">
          {SET_SUBTITLE}
        </p>
      </div>
      {/* Navigation */}
      <div className="flex justify-center gap-4">
        <button onClick={handlePrev} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-rose-700 rounded-lg font-semibold shadow transition-all duration-200">
          Previous
        </button>
        <button onClick={handleNext} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-rose-700 rounded-lg font-semibold shadow transition-all duration-200">
          Next
        </button>
      </div>
      {/* Flashcard */}
      <div className="flashcard-container max-w-lg mx-auto">
        <div
          className={`flashcard ${showAnswer ? 'flipped' : ''} ${cardBorder} rounded-3xl ${cardShadow} overflow-visible bg-white dark:bg-gray-900`}
          onClick={handleFlip}
          style={{ position: 'relative', width: '100%', height: '360px', background: 'none' }}
        >
          {/* Front of card */}
          <div className="flashcard-front flex flex-col items-center justify-center relative p-10">
            {/* Icon/Illustration */}
            <div className="absolute top-4 left-4 text-4xl select-none pointer-events-none drop-shadow-lg">
              {currentCard.icon || '🌸'}
            </div>
            {/* Audio Button */}
            <button
              onClick={e => { e.stopPropagation(); handlePlayAudio(); }}
              className="absolute top-4 right-4 w-12 h-12 bg-rose-100 hover:bg-rose-200 text-rose-800 rounded-full flex items-center justify-center shadow-lg border-2 border-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-400"
              disabled={audioLoading}
              title="Play pronunciation"
            >
              {audioLoading ? <span className="animate-spin">🎵</span> : '🎧'}
            </button>
            <div className={`${persianFont} text-gray-900 dark:text-white mb-4 mt-10 drop-shadow-xl`}>{currentCard.persian}</div>
            {divider}
            <div className="text-base text-gray-700 dark:text-gray-200 mt-8 font-medium tracking-wide">
              Click to see the meaning
            </div>
          </div>
          {/* Back of card */}
          <div className="flashcard-back flex flex-col items-center justify-center relative p-10">
            <div className="text-2xl font-bold text-indigo-800 mb-2 mt-10 drop-shadow-lg text-left w-full">
              {currentCard.transliteration}
            </div>
            <div className="text-xl font-semibold text-green-600 bg-green-50 rounded px-2 py-1 mb-2 drop-shadow-md text-left w-full">
              {currentCard.english}
            </div>
            {divider}
            {currentCard.explanation && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-2 text-gray-800 dark:text-gray-100 border border-amber-200 shadow text-left w-full">
                <span className="font-semibold text-rose-700">Meaning:</span> {currentCard.explanation}
              </div>
            )}
            {currentCard.example && (
              <div className="p-3 bg-gray-100 dark:bg-gray-900 rounded-lg text-gray-800 dark:text-gray-100 border border-rose-200 mt-2 shadow w-full">
                <span className="font-semibold text-rose-700">Example:</span> <span dir="rtl" className="block text-right mt-1">{currentCard.example}</span>
              </div>
            )}
            <div className="text-base text-gray-700 dark:text-gray-200 mt-4 font-medium tracking-wide text-left w-full">
              Click to go back
            </div>
          </div>
        </div>
      </div>
      {/* Card Counter */}
      <div className="text-center text-gray-700 dark:text-gray-200 font-semibold">
        {currentIndex + 1} / {proverbs.length}
      </div>
    </div>
  );
};

export default ProverbFlashcards; 